// pricing-calculator/utils/compat.ts
// Dependency, compatibility, and reason helpers for features/platforms.

import type {
  ID,
  Module,
  ModuleGroup,
  Platform,
} from '@dopomogai/types';

export type FeatureIndex = Record<ID, Module>;

/**
 * Build a flat index of features by ID from groups.
 */
export function buildFeatureIndex(groups: ModuleGroup[]): FeatureIndex {
  const idx: FeatureIndex = {};
  for (const g of groups) {
    for (const f of g.features) {
      idx[f.id] = f;
    }
  }
  return idx;
}

export function getFeatureLabel(index: FeatureIndex, id: ID): string {
  return index[id]?.label ?? String(id);
}

/**
 * Check platform compatibility for a feature.
 */
export function isFeatureCompatibleWithPlatform(feature: Module, platformId?: ID): boolean {
  if (!platformId) return true;
  const bad = feature.platformIncompatibilities || [];
  return !bad.includes(platformId);
}

/**
 * Returns a list of unmet dependency IDs for a feature, given the current selection set.
 */
export function getUnmetDependencies(feature: Module, selectedIds: Set<ID>): ID[] {
  const deps = feature.dependsOn || [];
  return deps.filter((d: ID) => !selectedIds.has(d));
}

/**
 * Human-readable reason why a feature is disabled, if any.
 */
export function getFeatureDisabledReason(
  feature: Module,
  platform?: Platform,
  selectedIds?: Set<ID>,
  index?: FeatureIndex
): string | undefined {
  if (platform && !isFeatureCompatibleWithPlatform(feature, platform.id)) {
    return `Not available on ${platform.label}`;
  }
  if (selectedIds && feature.dependsOn && feature.dependsOn.length > 0) {
    const unmet = getUnmetDependencies(feature, selectedIds);
    if (unmet.length > 0) {
      const names = unmet.map((id) => (index ? getFeatureLabel(index, id) : id)).join(', ');
      return `Requires: ${names}`;
    }
  }
  return undefined;
}

/**
 * Resolve dependencies when enabling a feature.
 * Returns a new selection set and the list of IDs that were added to satisfy deps.
 * Cycle-safe via visited set.
 */
export function addWithDependencies(
  selected: Set<ID>,
  target: Module,
  index: FeatureIndex
): { nextSelected: Set<ID>; added: ID[] } {
  const next = new Set(selected);
  const added: ID[] = [];
  const visited = new Set<ID>();

  function dfs(featureId: ID) {
    if (visited.has(featureId)) return;
    visited.add(featureId);

    const feat = index[featureId];
    if (!feat) return;

    // Ensure deps first
    const deps = feat.dependsOn || [];
    for (const dep of deps) {
      if (!next.has(dep)) {
        next.add(dep);
        added.push(dep);
      }
      dfs(dep);
    }
  }

  // Add the target itself (if not selected)
  if (!next.has(target.id)) {
    next.add(target.id);
  }

  // Then recursively add its dependencies
  dfs(target.id);

  return { nextSelected: next, added };
}

/**
 * Remove a feature from selection. If other features depend on it, optionally remove them too.
 * Returns nextSelected and removed list. If removeDependents=false, it keeps dependents and leaves state inconsistent;
 * use getDependents() to warn the user in UI.
 */
export function removeFeature(
  selected: Set<ID>,
  removeId: ID,
  index: FeatureIndex,
  removeDependents = false
): { nextSelected: Set<ID>; removed: ID[]; dependents: ID[] } {
  const next = new Set(selected);
  const removed: ID[] = [];
  const dependents = getDependents(index, removeId, next);

  if (removeDependents) {
    for (const dep of dependents) {
      if (next.delete(dep)) removed.push(dep);
    }
  }
  if (next.delete(removeId)) removed.push(removeId);

  return { nextSelected: next, removed, dependents };
}

/**
 * Find all features that (directly or transitively) depend on a given feature.
 */
export function getDependents(index: FeatureIndex, id: ID, within: Set<ID>): ID[] {
  const result = new Set<ID>();
  const graph: Record<ID, ID[]> = {};

  // Build reverse dependency graph only for currently selectable features
  for (const fid of Object.keys(index)) {
    const deps = index[fid].dependsOn || [];
    for (const d of deps) {
      if (!graph[d]) graph[d] = [];
      graph[d].push(fid as ID);
    }
  }

  function dfs(node: ID) {
    const arr = graph[node] || [];
    for (const child of arr) {
      if (within.has(child) && !result.has(child)) {
        result.add(child);
        dfs(child);
      }
    }
  }

  dfs(id);
  return Array.from(result);
}
  